import { eventUtcMs, formatTzTime, currentUser, shouldStampPrivate } from '../../functions/calendar/calendarUtils.js'
import { avatarUrl } from '../../../shared/functions/peopleUtils.js'

// Event types shown in the picker. `adminOnly` types are filtered out for
// non-admin users. The picker uses a colored dot (not the full pill bg) so
// it doesn't visually compete with the form fields.
const EVENT_TYPES = [
  { type: 'meeting', label: 'Meeting', color: 'green' },
  // Company event ("general") used to be admin-only in the picker, but the
  // role data isn't flowing into root.people yet — gating it here makes the
  // type invisible to everyone, including superadmin. RLS on the calendar
  // table is the real enforcement layer.
  { type: 'general', label: 'Company event', color: 'blue' },
  { type: 'busy', label: 'Unavailable', color: 'red' }
]


// --- Invitees autocomplete helpers -----------------------------------------
// `invitees` is the comma-separated list of accepted emails (each one a pill).
// `inviteeQuery` is the in-progress text in the input — drives suggestions
// but is NOT yet committed.

const _selectedEmails = (root) => {
  const inv = root?.newBooking?.invitees || ''
  return inv.split(',').map(x => x.trim()).filter(Boolean)
}

const _selectedPeople = (root) => {
  if (!root) return []
  const emails = new Set(_selectedEmails(root))
  return (root.people || []).filter(p => emails.has(p.email))
}

// Filter people by the current query text, excluding already-selected ones.
const _filterPeople = (root) => {
  const q = (root?.newBooking?.inviteeQuery || '').trim().toLowerCase()
  if (!q) return []
  const selected = new Set(_selectedEmails(root))
  const me = root.currentUserEmail
  return (root.people || [])
    .filter(p => p.email !== me && !selected.has(p.email) && (
      p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
    ))
    .slice(0, 6)
}

// Append a person's email as a pill, clear the in-progress query, keep focus.
const _addInvitee = (root, person) => {
  if (!person?.email) return
  const cur = _selectedEmails(root)
  if (cur.includes(person.email)) return
  const next = [...cur, person.email].join(', ')
  root.update({
    newBooking: { ...root.newBooking, invitees: next, inviteeQuery: '' }
  })
}

// Loose RFC-5322-ish email check — strict enough to reject typos, lax enough
// to accept anything postmaster-shaped.
const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const _isEmail = (s) => _EMAIL_RE.test(s.trim())

// Remove a pill by email.
const _removeInvitee = (root, email) => {
  const next = _selectedEmails(root).filter(e => e !== email).join(', ')
  root.update({ newBooking: { ...root.newBooking, invitees: next } })
}

// Backspace on empty input removes the last pill.
const _popInvitee = (root) => {
  const cur = _selectedEmails(root)
  if (!cur.length) return
  cur.pop()
  root.update({ newBooking: { ...root.newBooking, invitees: cur.join(', ') } })
}

// Closing the booking modal clears draft state. Also clears pendingSuggestion
// so a stale suggestion doesn't get marked applied if the user later creates
// an unrelated event.
export const CalNewBookingModal = {
  extends: 'Modal',
  stateKey: 'showNewBooking',
  extraClose: { pendingSuggestion: null },

  Window: {
    width: '100%',
    maxWidth: 'I', // ~800px
    maxHeight: '90dvh',
    gap: 'Z1',
    ModalHeader: {
      Hgroup: {
        H: { text: (el, s) => s.root?.newBooking?.editingEventId ? 'Edit Booking' : 'New Booking' },
        P: {
          text: (el, s) => s.root?.newBooking?.editingEventId
            ? 'Update the meeting time, attendees, or description'
            : 'Schedule a meeting and invite teammates'
        }
      }
    },

    Fields: {
      flow: 'y',
      gap: 'B',

      TitleField: {
        extends: 'InputField',
        Title: { text: 'Title' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: 'Add a title',
          autofocus: true,
          value: (el, s) => s.root?.newBooking?.title || '',
          // The DOM autofocus attribute only fires once on first mount —
          // when the modal re-opens (display flips none→flex) the existing
          // element doesn't refocus. Refocus only on the false→true edge of
          // `showNewBooking` so we don't fight the user when they click out
          // of the title field mid-edit.
          onCreate: (el) => {
            el._wasOpen = false
            requestAnimationFrame(() => { el.node?.focus?.() })
          },
          onUpdate: (el, s) => {
            const open = !!s.root?.showNewBooking
            if (open && !el._wasOpen) {
              requestAnimationFrame(() => { el.node?.focus?.() })
            }
            el._wasOpen = open
          },
          onInput: (e, el, s) => {
            s.root.update({ newBooking: { ...s.root?.newBooking, title: e.target.value } })
          }
        }
      },

      TypePicker: {
        flow: 'x',
        gap: 'Y',
        align: 'center left',
        flexWrap: 'wrap',
        childExtends: 'CalEventTypeBtn',
        childrenAs: 'state',
        children: (el, s) => {
          const admin = el.call('isAdmin')
          return EVENT_TYPES.filter(t => admin || !t.adminOnly)
        }
      },

      DateRow: {
        flow: 'x',
        gap: 'Z',
        colorScheme: 'dark',
        childExtends: 'InputField',

        DateField: {
          flex: '2',
          Title: { text: 'Date' },
          Input: {
            tag: 'input',
            type: 'date',
            value: (el, s) => s.root?.newBooking?.date || '',
            onInput: (e, el, s) => {
              s.root.update({ newBooking: { ...s.root?.newBooking, date: e.target.value } })
            }
          }
        },
        StartField: {
          flex: '1',
          Title: { text: 'Start' },
          Input: {
            tag: 'input',
            type: 'time',
            value: (el, s) => s.root?.newBooking?.startTime || '09:00',
            onInput: (e, el, s) => {
              s.root.update({ newBooking: { ...s.root?.newBooking, startTime: e.target.value } })
            }
          }
        },
        EndField: {
          flex: '1',
          Title: { text: 'End' },
          Input: {
            tag: 'input',
            type: 'time',
            value: (el, s) => s.root?.newBooking?.endTime || '10:00',
            onInput: (e, el, s) => {
              s.root.update({ newBooking: { ...s.root?.newBooking, endTime: e.target.value } })
            }
          }
        }
      },

      // Meeting location row — only shown for meeting/general events.
      // Three-option picker: Symbols Meet, External URL, Offline.
      MeetingModeRow: {
        flow: 'y',
        gap: 'Z',
        display: (el, s) => {
          const t = s.root?.newBooking?.type || 'meeting'
          return (t === 'meeting' || t === 'general') ? 'flex' : 'none'
        },

        ModePicker: {
          flow: 'x',
          gap: 'Y',
          flexWrap: 'wrap',
          childExtends: 'CalMeetingModeBtn',
          childrenAs: 'state',
          children: [
            { mode: 'symbols-meet', label: 'Symbols Meet', icon: 'video' },
            { mode: 'external-url', label: 'External URL', icon: 'link' },
            { mode: 'offline', label: 'Offline', icon: 'mapPin' }
          ]
        },

        UrlField: {
          extends: 'InputField',
          display: (el, s) => {
            const m = s.root?.newBooking?.meetingMode || 'symbols-meet'
            return m === 'external-url' ? 'flex' : 'none'
          },
          Title: { text: 'Meeting URL' },
          Input: {
            tag: 'input',
            type: 'url',
            placeholder: 'Paste Zoom, Google Meet, or other link',
            value: (el, s) => s.root?.newBooking?.meetUrl || '',
            onInput: (e, el, s) => {
              s.root.update({ newBooking: { ...s.root?.newBooking, meetUrl: e.target.value } })
            }
          }
        }
      },

      VisibilityRow: null,

      // Invitees: a plain text input on top, and the selected invitees rendered
      // as a separate grid of cards below. The input never holds pills — picking
      // a suggestion (or pressing Enter) adds the person to the grid and clears
      // the input. The suggestions dropdown is anchored to the input field.
      InviteesField: {
        extends: 'InputField',
        position: 'relative',
        Title: { text: 'Invitees' },
        Input: {
          tag: 'input',
          type: 'text',
          placeholder: (el, s) => _selectedEmails(s.root).length ? '' : 'Type a name…',
          attr: {
            autocomplete: 'off',
            spellcheck: 'false'
          },
          value: (el, s) => s.root?.newBooking?.inviteeQuery || '',
          onFocus: (e) => { try { e.target.select() } catch (_) {} },
          onInput: (e, el, s) => {
            s.root.update({ newBooking: { ...s.root?.newBooking, inviteeQuery: e.target.value } })
          },
          onKeydown: (e, el, s) => {
            const root = s.root
            const q = (root.newBooking?.inviteeQuery || '').trim()
            if ((e.key === 'Backspace' || e.key === 'Delete') && q === '') {
              e.preventDefault()
              _popInvitee(root)
              return
            }
            if (e.key === 'Tab' || e.key === 'Enter' || e.key === ',') {
              // Prefer the first autocomplete suggestion (known team member),
              // otherwise fall back to the raw query if it's a valid email —
              // that's how external attendees get added.
              const matches = _filterPeople(root)
              if (matches.length) {
                e.preventDefault()
                _addInvitee(root, matches[0])
              } else if (_isEmail(q)) {
                e.preventDefault()
                _addInvitee(root, { email: q })
              }
            }
          },
          onBlur: (e, el, s) => {
            // Commit a pending external email when the user clicks away
            // without pressing Enter.
            const q = (s.root?.newBooking?.inviteeQuery || '').trim()
            if (_isEmail(q) && !_filterPeople(s.root).length) {
              _addInvitee(s.root, { email: q })
            }
          }
        },

        // Selected invitees rendered as a grid of cards (not as inline pills
        // inside the input). Lets several invitees breathe and shows the per-
        // person local time without crowding the typing area.
        SelectedGrid: {
          display: (el, s) => _selectedPeople(s.root).length ? 'grid' : 'none',
          gridTemplateColumns: 'repeat(auto-fill, minmax(F, 1fr))',
          gap: 'Y',
          marginTop: 'Y',
          childExtends: 'CalInviteePill',
          childrenAs: 'state',
          children: (el, s) => _selectedPeople(s.root)
        },

        Suggestions: {
          display: (el, s) => _filterPeople(s.root).length ? 'flex' : 'none',
          position: 'absolute',
          top: '100%',
          left: '0',
          right: '0',
          marginTop: 'Y',
          flow: 'y',
          theme: 'modal',
          borderWidth: '1px',
          borderColor: 'line',
          borderStyle: 'solid',
          round: 'Z2',
          boxShadow: '0 8px 24px var(--color-shadow)',
          maxHeight: 'G',
          overflowY: 'auto',
          zIndex: 10,

          childExtends: 'CalInviteeSuggestion',
          childrenAs: 'state',
          children: (el, s) => _filterPeople(s.root)
        }
      },

      DescriptionField: {
        extends: 'InputField',
        Title: { text: 'Description' },
        Input: {
          tag: 'textarea',
          placeholder: 'Optional notes',
          fontFamily: 'inherit',
          fontSize: 'Z2',
          rows: 4,
          resize: 'vertical',
          value: (el, s) => s.root?.newBooking?.description || '',
          onInput: (e, el, s) => {
            s.root.update({ newBooking: { ...s.root?.newBooking, description: e.target.value } })
          }
        }
      },

      // Apple-style recurring picker — segmented presets + day toggles
      // when "Weekly" is selected.
      RecurringSection: {
        flow: 'y',
        gap: 'Z',

        Label: {
          tag: 'span',
          text: 'Repeat',
          fontSize: 'Z2',
          color: 'caption'
        },

        Presets: {
          flow: 'x',
          gap: 'Y',
          flexWrap: 'wrap',

          childExtends: 'CalRecurringBtn',
          childrenAs: 'state',
          children: [
            { value: 'none', label: 'Never' },
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'biweekly', label: 'Every 2 weeks' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'yearly', label: 'Yearly' }
          ]
        },

        // Day-of-week toggles, only visible when Weekly is picked. Allows
        // multi-select like Apple Calendar's custom rule (e.g. Mon/Wed/Fri).
        DayToggles: {
          display: (el, s) => s.root?.newBooking?.recurring === 'weekly' ? 'flex' : 'none',
          flow: 'x',
          gap: 'Y',
          marginTop: 'Y',

          childExtends: 'CalRecurringDayBtn',
          childrenAs: 'state',
          children: [
            { dow: 1, label: 'M' },
            { dow: 2, label: 'T' },
            { dow: 3, label: 'W' },
            { dow: 4, label: 'T' },
            { dow: 5, label: 'F' },
            { dow: 6, label: 'S' },
            { dow: 0, label: 'S' }
          ]
        }
      }
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z1',
      childExtends: 'Button',

      SaveBtn: {
        theme: 'primary',
        text: (el, s) => s.root?.newBooking?.editingEventId ? 'Update' : 'Save',
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          const root = s.root
          // For recurring events, show scope picker before saving
          const nb = root.newBooking || {}
          if (nb.editingEventId) {
            const orig = (root.events || []).find(ev => ev.id === nb.editingEventId)
            const baseId = orig?.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '')
            const hasSiblings = baseId && (root.events || []).some(ev =>
              ev.id !== nb.editingEventId && ev.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '') === baseId
            )
            if (hasSiblings && !nb._saveScope) {
              root.update({ newBooking: { ...nb, _showScopePicker: true } })
              return
            }
          }
          // Date is the only hard requirement; title is optional (e.g. for
          // a quick "busy" block where the type is enough context).
          if (!nb.date) return
          const inviteesArr = (nb.invitees || '')
            .split(',')
            .map(x => x.trim())
            .filter(Boolean)
          const type = nb.type || 'meeting'
          // Busy/unavailable blocks render via the per-user unavailable layer
          // (`getUnavailableRanges`), which only picks up an event if the
          // viewer is in its invitees list. Stamp the creator so the event
          // shows up on their own column without manual self-invite.
          if (type === 'busy' && root.currentUserEmail && !inviteesArr.includes(root.currentUserEmail)) {
            inviteesArr.push(root.currentUserEmail)
          }
          const recurring = nb.recurring || 'none'
          const meetingMode = nb.meetingMode || 'symbols-meet'
          // External URL meetings: append the provided link to description.
          // Symbols Meet rooms are created asynchronously after Supabase save.
          let description = nb.description || ''
          if ((type === 'meeting' || type === 'general') && meetingMode === 'external-url') {
            const link = (nb.meetUrl && nb.meetUrl.trim())
            if (link && !description.includes(link)) {
              description = description ? `${description}\n${link}` : link
            }
          }
          const resetBooking = {
            title: '',
            date: nb.date,
            startTime: '09:00',
            endTime: '10:00',
            invitees: '',
            inviteeQuery: '',
            description: '',
            type: 'meeting',
            meetingMode: 'symbols-meet',
            meetUrl: '',
            recurring: 'none',
            recurringDays: [],
            editingEventId: null
          }
          const payload = {
            title: nb.title || '',
            date: nb.date,
            startTime: nb.startTime || '09:00',
            endTime: nb.endTime || '10:00',
            allDay: !!nb.allDay,
            type,
            meetingMode,
            recurring,
            recurringDays: recurring === 'weekly' ? (nb.recurringDays || []) : [],
            description,
            invitees: inviteesArr,
            // Stamp the creator on first save so the move/resize permission
            // model can tell creator from participant later. Edits keep the
            // existing creator.
            creatorEmail: nb.editingEventId
              ? (root.events?.find(ev => ev.id === nb.editingEventId)?.creatorEmail || root.currentUserEmail)
              : root.currentUserEmail,
            // Privacy stamp: superadmin events (except company-wide) and
            // anything created by an external party are marked private so
            // other viewers see them as a generic Unavailable block.
            isPrivate: shouldStampPrivate(
              currentUser(root) || { email: root.currentUserEmail },
              type,
              root.people || []
            ),
            // Superadmin "Share with team" override — explicitly publishes
            // a private event to the team. Defaults to whatever the user
            // toggled in the modal (false otherwise).
            isTeamShared: !!nb.isTeamShared
          }
          if (nb.editingEventId) {
            const editedOriginal = (root.events || []).find(ev => ev.id === nb.editingEventId)
            const geid = editedOriginal?.googleEventId
            const baseId = geid?.replace(/_\d{8}T\d{6}Z$/, '')
            const scope = nb._saveScope || 'this'
            const sharedFields = { type: payload.type, description: payload.description, invitees: payload.invitees }

            const shouldUpdate = (ev) => {
              if (ev.id === nb.editingEventId) return true
              if (scope === 'this') return false
              const evBase = ev.googleEventId?.replace(/_\d{8}T\d{6}Z$/, '')
              if (evBase !== baseId) return false
              if (scope === 'all') return true
              // 'future': only events on or after the edited event's date
              return ev.date >= editedOriginal.date
            }

            const updated = (root.events || []).map(ev => {
              if (ev.id === nb.editingEventId) return { ...ev, ...payload }
              if (shouldUpdate(ev)) return { ...ev, ...sharedFields }
              return ev
            })
            root.update({
              events: updated,
              showNewBooking: false,
              newBooking: resetBooking
            })
            const edited = updated.find(ev => ev.id === nb.editingEventId)
            if (edited) el.call('saveEventToSupabase', edited)
            // Propagate shared fields to siblings in DB based on scope
            if (scope !== 'this' && edited) el.call('updateSharedEventFields', edited, sharedFields)
          } else {
            const newEv = { id: Date.now(), ...payload }
            // Capture before clearing so the .then closure can reference it.
            const pending = root.pendingSuggestion
            const _meetingMode = meetingMode
            root.update({
              events: [...(root.events || []), newEv],
              showNewBooking: false,
              newBooking: resetBooking,
              pendingSuggestion: null
            })
            // Persist to Supabase, then patch the optimistic id with the
            // real autoincrement id once the upsert returns.
            el.call('saveEventToSupabase', newEv).then(async (realId) => {
              if (!realId) return
              root.update({
                events: (root.events || []).map(ev =>
                  ev.id === newEv.id ? { ...ev, id: realId } : ev
                )
              })
              // Symbols Meet: create a room and attach link to event description
              if (_meetingMode === 'symbols-meet' && (type === 'meeting' || type === 'general')) {
                const roomId = await el.call('createRoom', {
                  name: payload.title || 'Calendar call',
                  kind: 'scheduled',
                  calendarEventId: realId,
                  transcribeEnabled: true
                })
                if (roomId) {
                  const meetLink = `${window.location.origin}/meet#room=${roomId}`
                  const updatedDesc = payload.description
                    ? `${payload.description}\n${meetLink}`
                    : meetLink
                  const patched = { ...payload, id: realId, description: updatedDesc }
                  root.update({
                    events: (root.events || []).map(ev =>
                      ev.id === realId ? { ...ev, description: updatedDesc } : ev
                    )
                  })
                  el.call('saveEventToSupabase', patched)
                }
              }
              // If this save originated from a meet transcript suggestion,
              // mark it applied so the card shows a checkmark on next visit.
              if (pending?.bucket === 'calendar_events' && pending?.item) {
                el.call('markSuggestionApplied', pending.bucket, pending.item, realId)
              }
            })
          }
        }
      }
    },

    // Scope picker overlay — shown when editing a recurring event
    ScopePicker: {
      if: (el, s) => s.root?.newBooking?._showScopePicker,
      position: 'absolute',
      inset: '0',
      zIndex: '10',
      background: 'black.65',
      backdropFilter: 'blur(4px)',
      align: 'center center',
      round: 'Z2',

      Dialog: {
        padding: 'B1',
        round: 'A',
        background: 'overlay',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'line',
        flow: 'y',
        gap: 'A',
        width: '320px',
        boxShadow: '0 16px 48px -8px rgba(0,0,0,0.5)',

        Title: {
          tag: 'h3',
          text: 'Edit recurring event',
          fontSize: 'A',
          fontWeight: '700',
          color: 'title',
          margin: '0'
        },

        Subtitle: {
          tag: 'p',
          text: 'Apply changes to:',
          fontSize: 'Z1',
          color: 'caption',
          margin: '0'
        },

        Options: {
          flow: 'y',
          gap: 'Z',
          childExtends: {
            tag: 'button',
            type: 'button',
            padding: 'Z1 A',
            round: 'Z',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'line',
            background: 'overlay',
            color: 'title',
            fontSize: 'Z1',
            fontWeight: '600',
            cursor: 'pointer',
            textAlign: 'left',
            text: (el, s) => s.label,
            ':hover': { background: 'overlayStrong', borderColor: 'caption' }
          },
          childrenAs: 'state',
          children: [
            { key: 'this', label: 'This event only' },
            { key: 'future', label: 'This and future events' },
            { key: 'all', label: 'All events in series' }
          ],

          onClick: (e, el, s) => {
            const root = s.root
            const nb = root.newBooking || {}
            root.update({
              newBooking: { ...nb, _showScopePicker: false, _saveScope: s.key }
            })
            // Trigger the save by clicking the SaveBtn programmatically
            const win = el.parent?.parent?.parent
            const saveBtn = win?.ModalFooter?.SaveBtn
            if (saveBtn?.node) saveBtn.node.click()
          }
        },

        CancelScopeBtn: {
          tag: 'button',
          type: 'button',
          text: 'Cancel',
          padding: 'Z A',
          background: 'none',
          borderWidth: '0',
          color: 'caption',
          fontSize: 'Z1',
          cursor: 'pointer',
          textAlign: 'center',
          onClick: (e, el, s) => {
            const root = s.root
            const nb = root.newBooking || {}
            root.update({ newBooking: { ...nb, _showScopePicker: false } })
          }
        }
      }
    }
  }
}

// Invitee pill — avatar + name + per-tz time + remove. Clicking the X
// removes the pill from the invitees list.
export const CalInviteePill = {
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Y Z',
  round: 'C',
  background: 'overlay',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    src: (el, s) => avatarUrl(s.name)
  },
  Name: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    text: (el, s) => (s.name || '').split(' ')[0]
  },
  // Live local time for this invitee, in their timezone, derived from the
  // current newBooking date+startTime+endTime.
  LocalTime: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontFamily: 'monospace',
    text: (el, s) => {
      const nb = el.state.root.newBooking
      if (!nb || !s.timezone) return ''
      const sMs = eventUtcMs(nb.date, nb.startTime || '00:00')
      const eMs = eventUtcMs(nb.date, nb.endTime || '00:00')
      if (!sMs) return ''
      return `${formatTzTime(sMs, s.timezone)}–${formatTzTime(eMs, s.timezone)}`
    }
  },
  RemoveBtn: {
    tag: 'button',
    text: '×',
    border: 'none',
    background: 'transparent',
    color: 'caption',
    cursor: 'pointer',
    padding: '0',
    fontSize: 'A',
    lineHeight: '1',
    ':hover': { color: 'title' },
    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      _removeInvitee(el.state.root, s.email)
    }
  }
}

// Single suggestion row in the invitees autocomplete dropdown. Shows the
// avatar + name + email; clicking selects this person.
export const CalInviteeSuggestion = {
  tag: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Z A',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  ':hover': { background: 'overlay' },
  onMousedown: (e, el, s) => {
    // Add the invitee on mousedown (before blur fires) so the click works
    // even though the input would normally lose focus.
    e.preventDefault()
    e.stopPropagation()
    _addInvitee(el.state.root, s)
    // Force-clear the input DOM value too, in case the reactive `value`
    // binding doesn't push the empty string back to a focused input.
    const root = el.state?.root
    const wrap = el.parent?.parent?.node || document
    const input = wrap.querySelector && wrap.querySelector('input[type="text"]')
    if (input) input.value = ''
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B B',
    src: (el, s) => avatarUrl(s.name)
  },
  Info: {
    flow: 'y',
    gap: 'Y2',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',

    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '600',
      color: 'title',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || ''
    },
    Email: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontFamily: 'monospace',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.email || ''
    }
  }
}

// Recurring preset button (Never / Daily / Weekly / …). Same shape as
// CalEventTypeBtn so the two pickers line up visually.
// Visibility pill for the superadmin "Private / Team" picker. Mirrors the
// recurring picker's shape so the rows align visually.
export const CalVisibilityBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Y',
  padding: 'Y2 Z',
  round: 'C',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  background: (el, s) => {
    const cur = !!el.state.root.newBooking?.isTeamShared
    return cur === s.value ? 'line' : 'transparent'
  },
  color: 'title',
  text: (el, s) => s.label,
  ':hover': { background: 'overlay' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      newBooking: { ...el.state.root.newBooking, isTeamShared: s.value }
    })
  }
}

export const CalRecurringBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  padding: 'Y2 A',
  round: 'C',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  background: (el, s) => {
    const cur = el.state.root.newBooking?.recurring || 'none'
    return cur === s.value ? 'line' : 'transparent'
  },
  color: 'title',
  text: (el, s) => s.label,
  ':hover': { background: 'overlay' },
  onClick: (e, el, s) => {
    e.preventDefault()
    const root = el.state.root
    const patch = { recurring: s.value }
    // Switching to Weekly seeds the day-toggles with the event's own day.
    if (s.value === 'weekly' && !(root.newBooking?.recurringDays?.length)) {
      const d = root.newBooking?.date
      if (d) {
        const [y, m, dy] = d.split('-').map(Number)
        patch.recurringDays = [new Date(y, m - 1, dy).getDay()]
      }
    }
    root.update({ newBooking: { ...root.newBooking, ...patch } })
  }
}

// Day-of-week toggle (M T W T F S S). Multi-select.
export const CalRecurringDayBtn = {
  tag: 'button',
  width: 'B1',
  height: 'B1',
  padding: '0',
  round: '50%',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  cursor: 'pointer',
  fontSize: 'Y2',
  fontWeight: '600',
  align: 'center center',
  background: (el, s) => {
    const days = el.state.root.newBooking?.recurringDays || []
    return days.includes(s.dow) ? 'blue' : 'transparent'
  },
  color: (el, s) => {
    const days = el.state.root.newBooking?.recurringDays || []
    return days.includes(s.dow) ? 'white' : 'caption'
  },
  text: (el, s) => s.label,
  onClick: (e, el, s) => {
    e.preventDefault()
    const root = el.state.root
    const cur = new Set(root.newBooking?.recurringDays || [])
    if (cur.has(s.dow)) cur.delete(s.dow)
    else cur.add(s.dow)
    root.update({
      newBooking: { ...root.newBooking, recurringDays: Array.from(cur).sort() }
    })
  }
}

// Type picker pill — colored dot indicator. When selected, the pill
// background fills with the type's color (instead of just border highlight)
// to match the recurring picker's "filled" active state.
export const CalEventTypeBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Z',
  padding: 'Y2 Z',
  round: 'C',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: (el, s) => {
    const cur = el.state.root.newBooking?.type || 'meeting'
    return cur === s.type ? 'transparent' : 'line'
  },
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  minWidth: 'F',
  background: (el, s) => {
    const cur = el.state.root.newBooking?.type || 'meeting'
    return cur === s.type ? 'line' : 'transparent'
  },
  color: (el, s) => {
    const cur = el.state.root.newBooking?.type || 'meeting'
    return cur === s.type ? s.color : 'title'
  },
  ':hover': { opacity: '.9' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      newBooking: { ...el.state.root.newBooking, type: s.type }
    })
  },

  Dot: {
    width: 'Z',
    height: 'Z',
    round: '50%',
    background: (el, s) => s.color
  },
  Label: { tag: 'span', text: (el, s) => s.label || s.type }
}

// Confirm-move dialog. Shown when a creator drags/resizes an event that has
// other participants — the creator can either Update the time directly (and
// notify everyone) or Propose the change (which leaves the original in place
// and adds a ghost duplicate at the new spot, awaiting confirmation).
export const CalConfirmMoveModal = {
  display: (el, s) => s.root?.confirmMove ? 'flex' : 'none',
  position: 'fixed',
  inset: '0',
  zIndex: 2100,
  align: 'center center',
  theme: 'modalFade',
  onClick: (e, el, s) => {
    if (e.target === el.node) {
      _cancelConfirmMove(s.root)
    }
  },

  Panel: {
    flow: 'y',
    gap: 'Z1',
    padding: 'A B',
    width: '100%',
    maxWidth: 'H',
    theme: 'modal',
    round: 'Z2',
    boxShadow: '0 8px 40px var(--color-shadow)',

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      text: 'This event has participants'
    },
    Body: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      color: 'caption',
      lineHeight: '1.5',
      text: 'Update the time directly, or propose the change and wait for participants to confirm.'
    },
    ModalFooter: {
      flow: 'x',
      align: 'center flex-end',
      gap: 'Z1',
      flexWrap: 'wrap',
      childExtends: 'Button',

      CancelBtn: {
        theme: 'secondary',
        text: 'Cancel',
        onClick: (e, el, s) => _cancelConfirmMove(s.root)
      },
      ProposeBtn: {
        theme: 'secondary',
        text: 'Propose',
        onClick: (e, el, s) => _proposeFromConfirm(s.root, el)
      },
      UpdateBtn: {
        theme: 'primary',
        text: 'Update',
        onClick: (e, el, s) => {
          // Commit the proposed move directly to the original event +
          // persist to Supabase + push to Google.
          const cm = s.root?.confirmMove
          if (!cm) return
          const events = s.root?.events || []
          const idx = events.findIndex(x => x.id === cm.eventId)
          if (idx === -1) {
            s.root.update({ confirmMove: null })
            return
          }
          const updated = events.slice()
          const movedEv = { ...events[idx], ...cm.next }
          updated[idx] = movedEv
          s.root.update({ events: updated, confirmMove: null })
          el.call('saveEventToSupabase', movedEv)
        }
      }
    }
  }
}

const _cancelConfirmMove = (root) => {
  // The original event was never mutated — just dismiss.
  root.update({ confirmMove: null })
}

const _proposeFromConfirm = (root, el) => {
  const cm = root.confirmMove
  if (!cm) return
  const events = root.events || []
  const orig = events.find(x => x.id === cm.eventId)
  if (!orig) {
    root.update({ confirmMove: null })
    return
  }
  // Add a proposal duplicate at the dragged-to position. The original is
  // untouched. Proposals are persisted to Supabase too so other tabs / Google
  // see the same shadow event until it's accepted or declined.
  const proposal = {
    ...orig,
    ...cm.next,
    id: Date.now(),
    isProposal: true,
    originalEventId: orig.id,
    proposedBy: root.currentUserEmail
  }
  root.update({ events: [...events, proposal], confirmMove: null })
  if (el) {
    el.call('saveEventToSupabase', proposal).then((realId) => {
      if (!realId) return
      root.update({
        events: (root.events || []).map(ev =>
          ev.id === proposal.id ? { ...ev, id: realId } : ev
        )
      })
    })
  }
}

// Meeting mode button — Symbols Meet / External URL / Offline. Same pill
// shape as the type and recurring pickers.
export const CalMeetingModeBtn = {
  tag: 'button',
  flow: 'x',
  align: 'center center',
  gap: 'Z',
  padding: 'Y2 Z',
  round: 'C',
  borderWidth: '1px',
  borderStyle: 'solid',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  minWidth: 'F',
  background: (el, s) => {
    const cur = el.state.root.newBooking?.meetingMode || 'symbols-meet'
    return cur === s.mode ? 'green' : 'transparent'
  },
  borderColor: (el, s) => {
    const cur = el.state.root.newBooking?.meetingMode || 'symbols-meet'
    return cur === s.mode ? 'green' : 'line'
  },
  color: (el, s) => {
    const cur = el.state.root.newBooking?.meetingMode || 'symbols-meet'
    return cur === s.mode ? 'white' : 'title'
  },
  text: (el, s) => s.label,
  ':hover': { background: 'overlay' },
  onClick: (e, el, s) => {
    e.preventDefault()
    el.state.root.update({
      newBooking: { ...el.state.root.newBooking, meetingMode: s.mode }
    })
  }
}

// Generate a fake but plausible Google Meet link. We don't talk to a real
// backend here, but the format matches Meet's xxx-yyyy-zzz pattern so it
// reads correctly in the description field.
const _genMeetLink = () => {
  const seg = (n) => Math.random().toString(36).slice(2, 2 + n)
  return `https://meet.google.com/${seg(3)}-${seg(4)}-${seg(3)}`
}
export { _genMeetLink }
